'use client';

import { useEffect, useId, useRef } from 'react';

declare global {
  interface Window {
    yaContextCb?: Array<() => void>;
    Ya?: {
      Context?: {
        AdvManager?: {
          render: (options: { blockId: string; renderTo: string }) => void;
        };
      };
    };
  }
}

type YandexRtbAdProps = {
  blockId: string;
  className?: string;
  containerId?: string;
};

export default function YandexRtbAd({ blockId, className, containerId }: YandexRtbAdProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const renderedRef = useRef(false);
  const reactId = useId();

  const renderTo =
    containerId ??
    `yandex_rtb_${blockId.replace(/[^a-zA-Z0-9_-]/g, '_')}_${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof window === 'undefined') return;
    let timeoutId: number | null = null;
    const renderAd = () => {
      if (renderedRef.current) return;
      if (!element.isConnected) return;

      renderedRef.current = true;
      window.Ya?.Context?.AdvManager?.render({
        blockId,
        renderTo,
      });
    };

    const queueOrRender = () => {
      if (window.Ya?.Context?.AdvManager) return renderAd();

      window.yaContextCb = window.yaContextCb || [];
      window.yaContextCb.push(renderAd);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        if (!window.Ya?.Context?.AdvManager && !renderedRef.current) {
          console.warn('[Yandex RTB] AdvManager is unavailable after waiting for context.js', {
            blockId,
            renderTo,
          });
        }
      }, 8000);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          queueOrRender();
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px' },
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [blockId, renderTo]);

  return <div ref={containerRef} id={renderTo} className={className} />;
}
